/**
 * ExplicitGrantsEditor
 *
 * UI for toggling `excludeFromWildcard` permissions on a superadmin user.
 * These permissions are NOT auto-granted by `platform.*` wildcards — each
 * one needs an explicit per-user grant. Lives inside UserModal alongside
 * the read-only PermissionMatrix.
 *
 * Until now those grants were edited by hand-rolled SQL (see the
 * "Special permissions" snippets in the project CLAUDE.md). This editor
 * replaces that workflow.
 */
import { ShieldCheckIcon } from '@heroicons/react/24/outline';
import type { Permission } from './PermissionMatrix';

interface ExplicitGrantsEditorProps {
  availablePermissions: Permission[];
  /** Full permissions array from the user record (may include both role
   * wildcards and explicit grants — we only render the explicit ones). */
  selected: string[];
  onChange: (next: string[]) => void;
}

export function ExplicitGrantsEditor({
  availablePermissions,
  selected,
  onChange,
}: ExplicitGrantsEditorProps) {
  // Preserve any non-managed permissions (role wildcards, partner_account
  // bypasses, etc.) so we never accidentally strip them when re-saving.
  const managedNames = new Set(availablePermissions.map((p) => p.name));
  const unmanaged = selected.filter((p) => !managedNames.has(p));
  const grantedManaged = new Set(
    selected.filter((p) => managedNames.has(p)),
  );

  const toggle = (name: string) => {
    const next = new Set(grantedManaged);
    if (next.has(name)) next.delete(name);
    else next.add(name);
    onChange([...unmanaged, ...next]);
  };

  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
        <span className="w-1.5 h-1.5 rounded-full bg-purple-500" />
        Explicit grants
        <span className="text-xs font-normal text-gray-500">
          (not auto-granted by role)
        </span>
      </h3>
      <p className="text-xs text-gray-500 mb-3">
        These permissions sit outside the <code>platform.*</code> wildcard and
        must be turned on per user. Used to gate features like AI Cost
        analytics, External Integrations, and Coverage Audit.
      </p>
      <div className="space-y-2">
        {availablePermissions.map((perm) => {
          const checked = grantedManaged.has(perm.name);
          const inputId = `explicit-grant-${perm.name.replace(/\./g, '-')}`;
          return (
            <label
              key={perm.name}
              htmlFor={inputId}
              className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors ${
                checked
                  ? 'border-purple-300 bg-purple-50/50'
                  : 'border-gray-200 hover:bg-gray-50'
              }`}
            >
              <input
                id={inputId}
                type="checkbox"
                checked={checked}
                onChange={() => toggle(perm.name)}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <code className="text-xs font-mono text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded">
                    {perm.name}
                  </code>
                  {checked && (
                    <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
                      <ShieldCheckIcon className="w-3.5 h-3.5" />
                      Granted
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-600 mt-1 leading-snug">
                  {perm.description}
                </p>
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );
}
